/**
 * RestoID — TOTP code entry.
 * ---------------------------------------------------------------------------
 * Same submit logic as the upstream TotpCodeVerification (useSendMfaPayload + the
 * `session.mfa.invalid_totp_code` error code) but surfaces a bad code as a TOAST
 * (the design pattern), not the cheap inline <ErrorMessage> the upstream renders.
 * Used by the TOTP binding + verification screens.
 */
import { MfaFactor } from '@logto/schemas';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ErrorHandlers } from '@/hooks/use-error-handler';
import useSendMfaPayload from '@/hooks/use-send-mfa-payload';
import useToast from '@/hooks/use-toast';
import VerificationCodeInput from '@/shared/components/VerificationCode';
import { type UserMfaFlow } from '@/types';

import { Button, Stack } from './restoid';

const CODE_LENGTH = 6;
const isReady = (code: string[]) => code.length === CODE_LENGTH && code.every(Boolean);

type Props =
  | { flow: UserMfaFlow.MfaBinding; verificationId: string }
  | { flow: UserMfaFlow.MfaVerification };

const TotpCodeEntry = (props: Props) => {
  const { t } = useTranslation();
  const { setToast } = useToast();
  const sendMfaPayload = useSendMfaPayload();
  const [code, setCode] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const errorHandlers: ErrorHandlers = useMemo(
    () => ({
      'session.mfa.invalid_totp_code': (error) => {
        setToast(error.message);
      },
    }),
    [setToast]
  );

  const submit = useCallback(
    async (value: string[]) => {
      if (isSubmitting) {
        return;
      }
      setIsSubmitting(true);
      await sendMfaPayload(
        { payload: { type: MfaFactor.TOTP, code: value.join('') }, ...props },
        errorHandlers,
        () => setCode([])
      );
      setIsSubmitting(false);
    },
    [isSubmitting, sendMfaPayload, errorHandlers, props]
  );

  return (
    <Stack gap={16}>
      <VerificationCodeInput
        name="totpCode"
        value={code}
        onChange={(next) => {
          setCode(next);
          if (isReady(next)) {
            void submit(next);
          }
        }}
      />
      <Button
        title={t('action.continue')}
        isLoading={isSubmitting}
        onClick={() => {
          if (isReady(code)) {
            void submit(code);
          }
        }}
      />
    </Stack>
  );
};

export default TotpCodeEntry;
